import React from "react";
import { Box, Typography, useMediaQuery, Button } from "@mui/material";
import { colors } from "styles/colors";
import theme from "styles/theme";
import { isJwtTokenValid } from "utils/helpers";
import { redirectToAppStore } from "utils/getStoreRedirect";

interface MobileBlockerProps {
  children: React.ReactNode;
  allowStudents?: boolean; // If true, allows students through on mobile
  bypassOnAuth?: boolean;  // If true, bypasses for any authenticated user
  skipAuthCheck?: boolean; // Skip auth check when already validated by ProtectedRoute
  forceStudentBlock?: boolean; // Force show student blocker when used from ProtectedRoute
  forceShowStudentMessage?: boolean; // Force show student message without auth (for login page)
}

const MobileBlocker: React.FC<MobileBlockerProps> = ({
  children,
  allowStudents = false,
  bypassOnAuth = false,
  skipAuthCheck = false,
  forceStudentBlock = false,
  forceShowStudentMessage = false
}) => {
  // Detect mobile and tablet devices
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // xs, sm

  // If not mobile, always render children
  if (!isMobile) {
    return <>{children}</>;
  }

  // Helper function to render student blocker UI
  const renderStudentBlocker = () => (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: colors.fontPrimary,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        // pt: '128px', // Removed top padding for center alignment
        zIndex: 9999,
        px: 4,
      }}
    >
      {/* Commented out app-specific copy - will be added once app is published
      <Typography
        sx={{
          color: colors.secondary,
          fontSize: '18px',
          fontWeight: 500,
          textAlign: 'center',
          mb: 3,
          lineHeight: 1.5,
        }}
      >
        To give you a faster, smoother and more personalized experience, ProSculpt is now available exclusively on our mobile app.
      </Typography>
      <Typography
        sx={{
          color: colors.secondary,
          fontSize: '16px',
          fontWeight: 400,
          textAlign: 'center',
          mb: 4,
        }}
      >
        Please download the app to continue exploring jobs and stay updated.
      </Typography>
      */}
      
      {/* Generic copy for platform not supported on mobile */}
      <Typography
        sx={{
          color: colors.secondary,
          fontSize: '18px',
          fontWeight: 500,
          textAlign: 'center',
          mb: 3,
          lineHeight: 1.5,
          px: 2,
        }}
      >
        This platform is not supported on mobile browsers. Please switch to a desktop or laptop for a better user experience.
      </Typography>
      {/* Download button commented out - will be added once app is published
      <Button
        variant="contained"
        sx={{
          backgroundColor: colors.primaryBlue,
          color: colors.secondary,
          fontWeight: 600,
          px: 4,
          py: 1.5,
          borderRadius: 2,
          textTransform: 'none',
          fontSize: '16px',
          '&:hover': {
            backgroundColor: colors.primaryBlue,
            opacity: 0.9,
          },
        }}
        onClick={() => {
          redirectToAppStore();
        }}
      >
        Download the App
      </Button>
      */}
    </Box>
  );

  // If forceShowStudentMessage is true (for login page), show student message without auth check
  if (forceShowStudentMessage) {
    return renderStudentBlocker();
  }

  // If skipAuthCheck is true, we're being used from ProtectedRoute for a student
  if (skipAuthCheck && forceStudentBlock) {
    return renderStudentBlocker();
  }

  // Check authentication and user role (only if not skipping)
  const { roles, hasExpired } = isJwtTokenValid();
  const isAuthenticated = !hasExpired && roles && roles.length > 0;
  const userRole = isAuthenticated && roles.length > 0 ? roles[0] : null;
  const isStudent = userRole === "student";

  // Bypass logic for authenticated users
  if (bypassOnAuth && isAuthenticated) {
    return <>{children}</>;
  }

  // Allow students on mobile if prop is enabled
  if (allowStudents && isStudent) {
    return <>{children}</>;
  }

  // Render student-specific mobile blocker
  if (isStudent) {
    return renderStudentBlocker();
  }

  // Show same student blocker for all other roles
  return renderStudentBlocker();
};

export default MobileBlocker;